import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useGetDatesQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import { selectProject, selectProjectSpecialDates } from 'state/workflowSlice';

import dayjs, { Dayjs } from 'dayjs';
import { InfoWalkmeCrumb } from 'features/info_hover/InfoHover';
import { findDate } from 'features/task_bar/DatePicker';
import { dimmed_white_color } from 'theme/cemTheme';

import FontAwesomeIcon from '../FontAwesomeIcon';
import { StlButton } from '../StlButton/StlButton';
import StlConfirmButtons from '../StlConfirmButtons';
import { parseDate } from '../datetimeUtils';
import { classNames } from '../utils';
import DateRangePicker from './DateRangePicker';
import { SpecialEventsMultiDateList } from './SpecialEventsDateList';
import { areDatesConsecutive, diffInCalendarDays } from './utils';

import commonStyles from './DateTimeSelector.module.css';
import styles from './MultiDayRangePicker.module.css';

export function DayRangePickerButton({
  handleClick,
}: {
  handleClick: () => void;
}) {
  return (
    <Box display={'flex'}>
      <Button
        fullWidth
        className={classNames(commonStyles.textBtn)}
        onClick={handleClick}
        sx={{ height: '1.25rem', width: '6.75rem' }}
      >
        <FontAwesomeIcon name="calendar-circle-plus" sx={{ padding: '6px' }} />
        <Typography variant="body2">Date Range</Typography>
      </Button>
      <InfoWalkmeCrumb elId="date-range-calendar" />
    </Box>
  );
}

export const MAX_RANGE_DAYS = 30;
const MIN_RANGE_DAYS = 2;

enum RangePickerTab {
  Calendar = 'calendar',
  SpecialEvents = 'special',
}

interface IDayRangePicker {
  targetDate?: string;
  endDate?: string;
  open: boolean;
  onClose: () => void;
  setDate: (start: string, end?: string) => void;
  forbiddenDates?: string[];
  monthsToShow?: number;
  selectRange?: boolean;
}

function MultiDayRangePickerPanel({
  targetDate,
  endDate,
  onClose,
  setDate,
  filterDate,
  minDate,
  maxDate,
  allowedDateSet,
  monthsShown,
  forbiddenDates,
  hasSpecialDates,
  openToDate,
}: {
  targetDate?: string;
  endDate?: string;
  onClose: () => void;
  setDate: (start: string, end?: string) => void;
  filterDate: (date: Date) => boolean;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  allowedDateSet: Set<string>;
  monthsShown: number;
  forbiddenDates?: string[];
  hasSpecialDates: boolean;
  openToDate: Dayjs;
}) {
  const specialDates = useAppSelector(selectProjectSpecialDates);

  const [activeTab, setActiveTab] = useState(RangePickerTab.Calendar);
  const [start, setStart] = useState<Dayjs | null>(() =>
    targetDate ? parseDate(targetDate) : null,
  );
  const [end, setEnd] = useState<Dayjs | null>(() =>
    endDate ? parseDate(endDate) : null,
  );
  const [selectedSpecialDates, setSelectedSpecialDates] = useState<Set<string>>(
    new Set(),
  );

  const sortedSpecialDates = useMemo(
    () => [...selectedSpecialDates].sort(),
    [selectedSpecialDates],
  );

  // any special date that falls within [start, end] gets auto-checked.
  useEffect(() => {
    if (!start || !end) return;
    const startStr = start.format('YYYY-MM-DD');
    const endStr = end.format('YYYY-MM-DD');
    const next = new Set<string>();
    for (const sdr of specialDates) {
      for (const d of sdr.dates) {
        if (d >= startStr && d <= endStr) next.add(d);
      }
    }
    setSelectedSpecialDates(next);
  }, [start, end, specialDates]);

  const selectedDayCount = start && end ? diffInCalendarDays(start, end) : 0;
  const rangeExceeded = selectedDayCount > MAX_RANGE_DAYS;
  const rangeTooShort =
    selectedDayCount > 0 && selectedDayCount < MIN_RANGE_DAYS;

  const specialSelectedCount = selectedSpecialDates.size;
  const specialTooFew = specialSelectedCount < MIN_RANGE_DAYS;
  const specialTooMany = specialSelectedCount > MAX_RANGE_DAYS;

  // the selected special dates must be consecutive
  const specialNotConsecutive =
    sortedSpecialDates.length >= MIN_RANGE_DAYS &&
    !areDatesConsecutive(sortedSpecialDates);
  const rangeNotConsecutive = useMemo(() => {
    if (!start || !end || selectedDayCount < MIN_RANGE_DAYS) return false;

    let current = start;
    while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
      if (!allowedDateSet.has(current.format('YYYY-MM-DD'))) return true;
      current = current.add(1, 'day');
    }
    return false;
  }, [start, end, selectedDayCount, allowedDateSet]);

  const applyRange = () => {
    if (!start || !end || rangeExceeded || rangeTooShort || rangeNotConsecutive)
      return;
    setDate(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
    onClose();
  };

  const applySpecialEvents = () => {
    if (specialTooFew || specialTooMany || specialNotConsecutive) return;
    setDate(
      sortedSpecialDates[0],
      sortedSpecialDates[sortedSpecialDates.length - 1],
    );
    onClose();
  };

  const handleToggleSpecialDate = (date: string) => {
    setSelectedSpecialDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else if (next.size < MAX_RANGE_DAYS) next.add(date);
      return next;
    });
  };

  const isCalendarTab = activeTab === RangePickerTab.Calendar;
  const selectionCount = isCalendarTab
    ? selectedDayCount
    : specialSelectedCount;
  const selectionTooShort = isCalendarTab ? rangeTooShort : specialTooFew;
  const selectionExceeded = isCalendarTab ? rangeExceeded : specialTooMany;
  const showConsecutiveError = isCalendarTab
    ? rangeNotConsecutive
    : specialNotConsecutive;
  const selectionInvalid =
    selectionTooShort || selectionExceeded || showConsecutiveError;

  let errorCaption: string;
  if (showConsecutiveError) {
    errorCaption = 'Dates must be available and consecutive';
  } else if (selectionTooShort) {
    errorCaption = 'Select at least 2 days';
  } else {
    errorCaption = `${selectionCount} out of ${MAX_RANGE_DAYS} days selected`;
  }

  const handleApply = () => {
    if (isCalendarTab) applyRange();
    else applySpecialEvents();
  };

  const handleReset = () => {
    setStart(null);
    setEnd(null);
    setSelectedSpecialDates(new Set());
  };

  const applyDisabled = isCalendarTab
    ? !start || !end || rangeExceeded || rangeTooShort || rangeNotConsecutive
    : specialTooFew || specialTooMany || specialNotConsecutive;

  return (
    <Stack
      className={classNames(commonStyles.withBorder, styles.twoMonthsCalendar)}
    >
      <Button
        onClick={onClose}
        className={commonStyles.textBtn}
        startIcon={<FontAwesomeIcon name="chevron-left" />}
      >
        <Typography variant="body2">Back</Typography>
      </Button>
      <Stack>
        <Typography variant="body2">Select Date Range</Typography>
        <Typography
          color={dimmed_white_color}
          variant="body2"
          fontSize="0.75rem"
        >
          Select up to
          <Box component="span" className={styles.bgBox}>
            {MAX_RANGE_DAYS} days
          </Box>
          . Metrics are aggregated across all selected days.
        </Typography>
      </Stack>
      {hasSpecialDates && (
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          className={styles.rangeTabs}
        >
          <Tab
            value={RangePickerTab.Calendar}
            label="Regular Days"
            disableRipple
          />
          <Tab
            value={RangePickerTab.SpecialEvents}
            label="Special Events"
            disableRipple
          />
        </Tabs>
      )}
      {activeTab === RangePickerTab.Calendar && (
        <DateRangePicker
          showYearSelector={false}
          maxDays={MAX_RANGE_DAYS}
          minDate={minDate}
          maxDate={maxDate}
          startValue={start}
          endValue={end}
          openToDate={openToDate}
          onChange={([s, e]) => {
            setStart(s);
            setEnd(e);
          }}
          filterDate={filterDate}
          monthsShown={monthsShown}
          extraWrapper={false}
        />
      )}
      {activeTab === RangePickerTab.SpecialEvents && (
        <Stack gap={0.5} className={styles.specialEventsTab}>
          <SpecialEventsMultiDateList
            columns={4}
            selectedDates={selectedSpecialDates}
            onToggleDate={handleToggleSpecialDate}
            forbiddenDates={forbiddenDates}
            maxSelection={MAX_RANGE_DAYS}
          />
        </Stack>
      )}
      <Box className={styles.rangeFooter}>
        <Box className={styles.infoRow}>
          <Typography
            color={selectionInvalid ? 'error' : dimmed_white_color}
            variant="body2"
            fontSize="0.75rem"
            alignContent="center"
          >
            {errorCaption}
          </Typography>
          <StlButton
            variant="text"
            value="Reset"
            width={52}
            handleClick={handleReset}
          />
        </Box>
        <StlConfirmButtons
          width={64}
          disabled={applyDisabled}
          handleApply={handleApply}
          handleCancel={onClose}
        />
      </Box>
    </Stack>
  );
}

export default function MultiDayRangePicker({
  targetDate,
  endDate,
  onClose,
  open,
  setDate,
  forbiddenDates,
  monthsToShow = 2,
  selectRange = true,
}: IDayRangePicker) {
  const project_slug = useAppSelector(selectProject);
  const specialDates = useAppSelector(selectProjectSpecialDates);
  const hasSpecialDates = (specialDates?.length ?? 0) > 0;
  const { data: rawDatesInfo } = useGetDatesQuery(project_slug, {
    skip: !project_slug,
    pollingInterval: 3 * 60 * 60 * 1000, // every 3 hours
  });

  const datesInfo = useMemo(() => {
    if (!rawDatesInfo) return undefined;

    const forbidden = forbiddenDates ?? [];
    if (selectRange) {
      return rawDatesInfo.filter(
        (info) => !forbidden.includes(info.date) && info.is_nrt,
      );
    }
    return rawDatesInfo.filter((info) => !forbidden.includes(info.date));
  }, [rawDatesInfo, forbiddenDates, selectRange]);

  const { vnrtOrRtDates, nrtDates } = useMemo(() => {
    const vnrtOrRt = new Set<string>();
    const nrt = new Set<string>();
    for (const info of rawDatesInfo ?? []) {
      (info.is_nrt ? nrt : vnrtOrRt).add(info.date);
    }
    return { vnrtOrRtDates: vnrtOrRt, nrtDates: nrt };
  }, [rawDatesInfo]);

  // memoize list of allowed dates as strings
  const allowedDateSet: Set<string> = useMemo(
    () => new Set((datesInfo ?? []).map((info) => info.date)),
    [datesInfo],
  );

  const filterDate = useCallback(
    (date: Date) => allowedDateSet.has(dayjs(date).format('YYYY-MM-DD')),
    [allowedDateSet],
  );

  const { minDate, maxDate } = useMemo(() => {
    let min: dayjs.Dayjs | undefined;
    let max: dayjs.Dayjs | undefined;
    for (const d of allowedDateSet) {
      const date = dayjs(d);
      if (!min || date.isBefore(min)) min = date;
      if (!max || date.isAfter(max)) max = date;
    }
    return { minDate: min, maxDate: max };
  }, [allowedDateSet]);

  const defaultDate = useMemo(() => {
    const dates = datesInfo ?? [];
    return dates.length > 0 ? dates[dates.length - 1].date : undefined;
  }, [datesInfo]);

  const openToDate = useMemo(() => {
    if (targetDate && datesInfo && findDate(dayjs(targetDate), datesInfo)) {
      return parseDate(targetDate).startOf('month');
    }
    if (defaultDate) return parseDate(defaultDate).startOf('month');
    return dayjs().startOf('month');
  }, [targetDate, defaultDate, datesInfo]);

  const handleDateChange = useCallback(
    ([start, end]: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
      let current = start;
      // check if selected range exceeds the maximum allowed days
      const selectedDayCount =
        start && end ? diffInCalendarDays(start, end) : 0;
      const rangeExceeded = selectedDayCount > MAX_RANGE_DAYS;
      if (rangeExceeded) return;
      // check if selected range is consecutive
      while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
        if (!allowedDateSet.has(current.format('YYYY-MM-DD'))) return;
        current = current.add(1, 'day');
      }
      if (start && end) {
        setDate(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
      } else if (start) {
        setDate(start.format('YYYY-MM-DD'), null);
      }
      onClose();
    },
    [setDate, onClose],
  );

  useEffect(() => {
    if (selectRange || !defaultDate || !datesInfo?.length) return;
    if (!targetDate || !findDate(dayjs(targetDate), datesInfo))
      setDate(defaultDate);
  }, [selectRange, targetDate, defaultDate, datesInfo, setDate]);

  if (!open) return null;

  // used for a single day selection or a range selection with Shift pressed
  if (!selectRange) {
    return (
      <DateRangePicker
        showYearSelector={true}
        minDate={minDate}
        maxDate={maxDate}
        startValue={targetDate ? parseDate(targetDate) : undefined}
        endValue={endDate ? parseDate(endDate) : undefined}
        openToDate={openToDate}
        onChange={handleDateChange}
        filterDate={filterDate}
        vnrtOrRtDates={vnrtOrRtDates}
        nrtDates={nrtDates}
        monthsShown={monthsToShow}
        selectRange={false}
        extraWrapper={false}
        maxDays={MAX_RANGE_DAYS}
        minDays={MIN_RANGE_DAYS}
        showShiftCaption
      />
    );
  }

  return (
    <MultiDayRangePickerPanel
      key={`${targetDate ?? ''}-${endDate ?? ''}`}
      targetDate={targetDate}
      endDate={endDate}
      onClose={onClose}
      setDate={setDate}
      filterDate={filterDate}
      minDate={minDate}
      maxDate={maxDate}
      allowedDateSet={allowedDateSet}
      monthsShown={monthsToShow}
      forbiddenDates={forbiddenDates}
      hasSpecialDates={hasSpecialDates}
      openToDate={openToDate}
    />
  );
}
